import { Globe, ChevronDown, Check } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { Language } from '@/i18n/translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { localizeInternalUrl } from '@/lib/languageRouting';

const languages: { code: Language; label: string; flag: string }[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

interface LanguageSelectorProps {
  compact?: boolean;
}

export function LanguageSelector({ compact = false }: LanguageSelectorProps) {
  const { language } = useLanguage();

  const selectLanguage = (nextLanguage: Language) => {
    if (nextLanguage === language) return;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.history.pushState(window.history.state, '', localizeInternalUrl(currentUrl, nextLanguage));
    window.location.reload();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={`flex items-center gap-1.5 text-xs font-semibold text-white bg-transparent hover:bg-accent border border-white hover:border-accent rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent/30 group ${compact ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
        <Globe className="w-4 h-4 text-white group-hover:text-white transition-colors duration-200" />
        <span className="uppercase tracking-wide text-white">{language}</span>
        {!compact && (
          <ChevronDown className="w-3 h-3 text-white/70 group-hover:text-white transition-colors duration-200" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="min-w-[160px] bg-background border border-border shadow-lg rounded-lg p-1 animate-in fade-in-0 slide-in-from-top-2 duration-150 z-50"
        sideOffset={8}
      >
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => selectLanguage(lang.code)}
            className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-all duration-150 ${
              language === lang.code 
                ? 'bg-secondary/10 text-secondary' 
                : 'hover:bg-muted'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-lg leading-none">{lang.flag}</span>
              <span className={`text-sm ${language === lang.code ? 'font-semibold' : 'font-normal text-foreground'}`}>
                {lang.label}
              </span>
            </div>
            {language === lang.code && (
              <Check className="w-4 h-4 text-secondary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
